import { Head } from "@inertiajs/react"
import { useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

import ScheduleForm from "./_form"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface UserOption {
  id: number
  name: string
}

interface ScheduleData {
  title: string
  notes: string
  start_date: string | null
  end_date: string | null
  no_start: boolean
  no_end: boolean
  leader_id: number | null
  investigator_ids: number[]
}

interface Props {
  case_record: CaseRecord
  schedule: ScheduleData
  leaders: UserOption[]
  investigators: UserOption[]
  errors?: Record<string, string[]>
}

export default function New({
  case_record,
  schedule,
  leaders,
  investigators,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Schedule", href: `/cases/${case_record.id}/schedules` },
    { title: "Add", href: `/cases/${case_record.id}/schedules/new` },
  ]

  const { data, setData, post, processing, transform } = useForm<ScheduleData>({
    title: schedule.title,
    notes: schedule.notes,
    start_date: schedule.start_date,
    end_date: schedule.end_date,
    no_start: schedule.no_start,
    no_end: schedule.no_end,
    leader_id: schedule.leader_id,
    investigator_ids: schedule.investigator_ids,
  })

  transform((data) => ({ case_schedule: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/cases/${case_record.id}/schedules`)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Add Schedule - ${case_record.case_number}`} />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">
          Add Scheduled Work for {case_record.display_name}
        </h1>
        <ScheduleForm
          caseId={case_record.id}
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Add"
          leaders={leaders}
          investigators={investigators}
        />
      </div>
    </AppLayout>
  )
}
